import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { FaceTracker } from "@/lib/faceTracker";
import { PoseTracker } from "@/lib/poseTracker";
import { AudioManager, TRACKS } from "@/lib/audioManager";
import { buildRoom } from "@/lib/room3d";
import StartScreen from "@/components/StartScreen";
import HUD from "@/components/HUD";
import TrackPicker from "@/components/TrackPicker";
import DeviceGuard from "@/components/DeviceGuard";
import { startHomepageFavicon, setRoomActiveFavicon, stopFavicon } from "@/lib/faviconManager";

// Room dims must match buildRoom (W=8, D=8, H=4.2)
const SIDES = {
  south: { pos: [0, 2.2, 3.4],  yaw: 0 },
  north: { pos: [0, 2.2, -3.4], yaw: Math.PI },
  east:  { pos: [3.4, 2.2, 0],  yaw: -Math.PI / 2 },
  west:  { pos: [-3.4, 2.2, 0], yaw: Math.PI / 2 },
};

function getResponsiveFov(aspect) {
  if (aspect >= 1.5) return 70;
  return Math.min(84, 70 + (1.5 - Math.max(0.8, aspect)) * 16);
}

export default function Experience() {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const trackerRef = useRef(null);
  const poseTrackerRef = useRef(null);
  const audioRef = useRef(null);
  const cleanupRef = useRef(null);

  const [started, setStarted] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [face, setFace] = useState({ x: 0, y: 0, z: 0, hasFace: false });
  const [levels, setLevels] = useState({ music: 0, ambient: 0 });
  const [currentTrack, setCurrentTrack] = useState(0);
  const [currentSide, setCurrentSide] = useState("south");
  const [musicVolume, setMusicVolume] = useState(0.7);
  const [surroundSound, setSurroundSound] = useState(false);
  const [audioHint, setAudioHint] = useState("look right to hear it");

  // Refs used by the animation loop
  const faceStateRef = useRef({ x: 0, y: 0, z: 0, hasFace: false });
  const levelsRef = useRef({ music: 0, ambient: 0 });
  const musicVolumeRef = useRef(musicVolume);
  const surroundRef = useRef(surroundSound);
  const audioHintRef = useRef("look right to hear it");
  const baseCamRef = useRef({ pos: new THREE.Vector3(...SIDES.south.pos), yaw: SIDES.south.yaw });
  useEffect(() => { musicVolumeRef.current = musicVolume; }, [musicVolume]);
  useEffect(() => {
    surroundRef.current = surroundSound;
    if (audioRef.current) {
      audioRef.current.setSurround(surroundSound);
    }
  }, [surroundSound]);

  // Favicon dynamic state: Grooving dancer on homepage, static locked HUD inside room
  useEffect(() => {
    if (started) {
      setRoomActiveFavicon();
    } else {
      startHomepageFavicon();
    }
    return () => stopFavicon();
  }, [started]);

  const handleToggleSurround = (val) => {
    setSurroundSound(val);
  };

  const handlePickTrack = async (i) => {
    if (!audioRef.current) return;
    setCurrentTrack(i);
    await audioRef.current.setTrack(i);
  };

  const handleSwitchSide = (side) => {
    if (!SIDES[side]) return;
    setCurrentSide(side);
    baseCamRef.current.pos.set(...SIDES[side].pos);
    baseCamRef.current.yaw = SIDES[side].yaw;
  };

  const handleBackToStart = () => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    if (poseTrackerRef.current) { poseTrackerRef.current.stop(); poseTrackerRef.current = null; }
    if (trackerRef.current) { trackerRef.current.stop(); trackerRef.current = null; }
    if (audioRef.current)   { audioRef.current.stop();   audioRef.current = null; }
    setStarted(false);
    setStatus("");
    setError(null);
    setFace({ x: 0, y: 0, z: 0, hasFace: false });
    setLevels({ music: 0, ambient: 0 });
  };

  const handleStart = async (side) => {
    setError(null);
    setStatus("starting");
    try {
      await launchExperience(side || "south");
      setCurrentSide(side || "south");
      setStarted(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to start");
      setStatus("error");
    }
  };

  const launchExperience = async (side) => {
    // 1) Audio
    const audio = new AudioManager({
      onTrackChange: (newIdx) => {
        setCurrentTrack(newIdx);
      },
    });
    await audio.start();
    audio.setSurround(surroundRef.current);
    audioRef.current = audio;

    // 2) Three.js scene FIRST (so we have setPoseLandmarks ready before tracker starts)
    const { scene, update, soundSources, setPoseLandmarks } = buildRoom({
      videoEl: videoRef.current,
    });

    // 3) FaceMesh for head-pose tracking (reliable x/y/z)
    const tracker = new FaceTracker({
      videoEl: videoRef.current,
      onUpdate: (data) => {
        faceStateRef.current = data;
        setFace(data);
      },
      onStatus: (s) => setStatus(s),
    });
    trackerRef.current = tracker;
    await tracker.start();

    // 4) Pose (separate model) — feeds body landmarks to the silhouette canvas
    const poseTracker = new PoseTracker({
      videoEl: videoRef.current,
      onLandmarks: (lm) => setPoseLandmarks(lm),
    });
    poseTrackerRef.current = poseTracker;
    // start pose after face model to avoid init contention
    poseTracker.start().catch((e) => console.warn("pose tracker start failed:", e));

    setCurrentTrack(audio.currentTrackIndex >= 0 ? audio.currentTrackIndex : 0);

    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    // seed base cam from chosen side
    baseCamRef.current.pos.set(...SIDES[side].pos);
    baseCamRef.current.yaw = SIDES[side].yaw;

    const initialAspect = window.innerWidth / window.innerHeight;
    const camera = new THREE.PerspectiveCamera(getResponsiveFov(initialAspect), initialAspect, 0.1, 100);
    camera.position.copy(baseCamRef.current.pos);

    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const asp = w / h;
      camera.aspect = asp;
      camera.fov = getResponsiveFov(asp);
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    let last = performance.now();
    let raf = 0;
    const forward = new THREE.Vector3();

    // Camera state (second-order low-pass). Includes an animated base yaw/pos so
    // switching sides at runtime slides smoothly.
    const camState = { yaw: 0, pitch: 0, dolly: 0 };
    const animBase = { pos: baseCamRef.current.pos.clone(), yaw: baseCamRef.current.yaw };
    const camLerp = 0.22;
    const camLerpZ = 0.16;
    const baseLerp = 0.08;

    const loop = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      update(dt);

      // Head-pose target signals
      const f = faceStateRef.current;
      const targetYaw = f.x * 1.9;
      const targetPitch = f.y * 1.3;
      // Depth dolly: responsive depth perception with subtle hint
      const targetDolly = Math.max(-0.40, Math.min(0.85, f.z * 1.05));

      // Snappy, responsive second-order tracking
      camState.yaw   += (targetYaw   - camState.yaw)   * camLerp;
      camState.pitch += (targetPitch - camState.pitch) * camLerp;
      camState.dolly += (targetDolly - camState.dolly) * camLerpZ;

      // Smoothly interpolate the base (used for in-room side switching)
      animBase.pos.lerp(baseCamRef.current.pos, baseLerp);
      // shortest-path yaw lerp
      const targetBaseYaw = baseCamRef.current.yaw;
      let dyaw = targetBaseYaw - animBase.yaw;
      while (dyaw > Math.PI) dyaw -= Math.PI * 2;
      while (dyaw < -Math.PI) dyaw += Math.PI * 2;
      animBase.yaw += dyaw * baseLerp;

      // Subtle gaze coupling: lean in slightly towards where you are looking (70% base wall, 30% gaze)
      const dollyYaw = animBase.yaw + camState.yaw * 0.3;
      const dollyForwardX = Math.sin(dollyYaw);
      const dollyForwardZ = -Math.cos(dollyYaw);
      camera.position.set(
        animBase.pos.x + dollyForwardX * camState.dolly,
        animBase.pos.y,
        animBase.pos.z + dollyForwardZ * camState.dolly,
      );

      // Safe room boundary clamp: prevent camera from ever clipping into walls
      camera.position.x = Math.max(-3.65, Math.min(3.65, camera.position.x));
      camera.position.z = Math.max(-3.65, Math.min(3.65, camera.position.z));

      const totalYaw = animBase.yaw + camState.yaw;
      const cp = Math.cos(camState.pitch);
      const target = new THREE.Vector3(
        camera.position.x + Math.sin(totalYaw) * cp,
        camera.position.y + Math.sin(camState.pitch),
        camera.position.z - Math.cos(totalYaw) * cp
      );
      camera.lookAt(target);

      // Audio & HUD: Dynamic spatial acoustics (distance + gaze + stereo panning)
      camera.getWorldDirection(forward);

      // Camera horizontal right vector (for exact stereo pan)
      const right = new THREE.Vector3(-forward.z, 0, forward.x);

      const toWindow = new THREE.Vector3().subVectors(soundSources.window, camera.position);
      const distWindow = toWindow.length() || 1;
      const normWindow = toWindow.clone().divideScalar(distWindow);

      const toRecord = new THREE.Vector3().subVectors(soundSources.recordPlayer, camera.position);
      const distRecord = toRecord.length() || 1;
      const normRecord = toRecord.clone().divideScalar(distRecord);

      // Dot product: how directly is camera facing the source?
      const dotRecord = forward.x * normRecord.x + forward.z * normRecord.z;
      const dotWindow = forward.x * normWindow.x + forward.z * normWindow.z;

      // Stereo pan: -1.0 (hard left) to +1.0 (hard right)
      const panRecord = Math.max(-1, Math.min(1, right.x * normRecord.x + right.z * normRecord.z));
      const panWindow = Math.max(-1, Math.min(1, right.x * normWindow.x + right.z * normWindow.z));

      // Physical distance attenuation:
      // When standing at West wall (d ~ 0.6m from window): window is 100% loud, record is ~25% distant.
      // When standing at East wall (d ~ 0.6m from record): record is 100% loud, window is ~25% distant.
      const distGainRecord = Math.min(1.0, Math.max(0.18, Math.pow(2.6 / Math.max(1.0, distRecord), 1.15)));
      const distGainWindow = Math.min(1.0, Math.max(0.18, Math.pow(2.6 / Math.max(1.0, distWindow), 1.15)));

      // Distance-adaptive minimum gaze floor:
      // If right next to a source (d < 1.5m), it stays loud and prominent even if facing away.
      // If far away (d > 4.5m), turning away fades it cleanly down to 6%.
      const minGazeRecord = Math.max(0.06, Math.min(0.85, 1.0 - (distRecord - 1.2) / 4.5));
      const minGazeWindow = Math.max(0.06, Math.min(0.85, 1.0 - (distWindow - 1.2) / 4.5));

      const facingRecord = (Math.max(-1, Math.min(1, dotRecord)) + 1) / 2;
      const facingWindow = (Math.max(-1, Math.min(1, dotWindow)) + 1) / 2;

      const gazeRecord = minGazeRecord + (1 - minGazeRecord) * Math.pow(facingRecord, 1.8);
      const gazeWindow = minGazeWindow + (1 - minGazeWindow) * Math.pow(facingWindow, 1.8);

      const isSurround = surroundRef.current;
      const musicLevel = isSurround ? 1.0 : Math.min(1, Math.max(0, distGainRecord * gazeRecord * 1.55));
      const ambientLevel = Math.min(1, Math.max(0, distGainWindow * gazeWindow * 1.55));
      levelsRef.current = { music: musicLevel, ambient: ambientLevel };

      // Apply master volume, dynamic levels, and stereo panning
      audio.setMusicVolume(musicVolumeRef.current);
      audio.setLevels({
        music: musicLevel,
        ambient: ambientLevel,
        panMusic: isSurround ? 0.0 : panRecord,
        panAmbient: panWindow,
      });

      // Real-time dynamic direction hint based on actual head/camera angle to record player
      let hint = "";
      if (isSurround) {
        hint = "all-around immersion";
      } else if (dotRecord > 0.72) {
        hint = "facing record player";
      } else if (dotRecord < -0.65) {
        hint = "turn around to hear it";
      } else if (panRecord > 0.25) {
        hint = "look right to hear it";
      } else if (panRecord < -0.25) {
        hint = "look left to hear it";
      } else {
        hint = "look ahead to hear it";
      }
      audioHintRef.current = hint;

      const up = new THREE.Vector3(0, 1, 0);
      audio.updateListener(camera.position, forward, up);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };

    const hudInterval = setInterval(() => {
      setLevels({ ...levelsRef.current });
      setAudioHint(audioHintRef.current);
    }, 66);

    raf = requestAnimationFrame(loop);

    cleanupRef.current = () => {
      cancelAnimationFrame(raf);
      clearInterval(hudInterval);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => { if (m.map) m.map.dispose(); m.dispose(); });
        }
      });
    };
  };

  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
      if (poseTrackerRef.current) poseTrackerRef.current.stop();
      if (trackerRef.current) trackerRef.current.stop();
      if (audioRef.current) audioRef.current.stop();
    };
  }, []);

  return (
    <DeviceGuard>
      <div style={{ position: "fixed", inset: 0, background: "var(--paper)", overflow: "hidden" }} className="grain">
        <canvas
          ref={canvasRef}
          data-testid="scene-canvas"
          className="canvas-cursor"
          style={{ position: "absolute", inset: 0, width: "100vw", height: "100vh", display: started ? "block" : "none" }}
        />

        {/* Small webcam preview in the corner (mirrored). Always mounted (tracker keeps a ref). */}
        <div
          style={{
            position: "fixed",
            bottom: "clamp(16px, 2.5vh, 22px)",
            right: "clamp(16px, 2.5vw, 22px)",
            width: "clamp(120px, 11vw, 160px)",
            height: "clamp(90px, 8.25vw, 120px)",
            borderRadius: 10,
            overflow: "hidden",
            zIndex: 6,
            border: "2px solid var(--ink)",
            background: "#000",
            visibility: started ? "visible" : "hidden",
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
          }}
        >
          <video
            ref={videoRef}
            data-testid="camera-preview"
            autoPlay
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
          />
          <div className="font-mono" style={{ position: "absolute", top: 6, left: 8, fontSize: 9, color: "var(--neon-cyan)", letterSpacing: "0.2em", textTransform: "uppercase", textShadow: "0 0 6px rgba(0,211,217,0.7)" }}>
            ● cam.in
          </div>
        </div>

        {started && (
          <HUD
            face={face}
            levels={levels}
            currentSide={currentSide}
            onSwitchSide={handleSwitchSide}
            onExit={handleBackToStart}
            surround={surroundSound}
          />
        )}
        {started && (
          <TrackPicker
            tracks={TRACKS}
            currentIndex={currentTrack}
            onPick={handlePickTrack}
            volume={musicVolume}
            onVolumeChange={setMusicVolume}
            surround={surroundSound}
            onToggleSurround={handleToggleSurround}
            directionHint={audioHint}
          />
        )}

        {!started && <StartScreen onStart={handleStart} status={error || status} />}
      </div>
    </DeviceGuard>
  );
}
